function throttle (func, wait = 100) {
  let timer = null
  return function (...args) {
    if (timer === null) {
      timer = setTimeout(() => {
        func.apply(this, args)
        timer = null
      }, wait)
    }
  }
}

function debounce (fn, wait) {
  let t
  return (...args) => {
    clearTimeout(t)
    t = setTimeout(() => fn.apply(this, args), wait)
  }
}

const serializeForm = form => {
  const obj = {}
  const formData = new FormData(form)
  for (const key of formData.keys()) {
    obj[key] = formData.get(key)
  }
  return JSON.stringify(obj)
}

function fetchConfig (type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` }
  }
}

function getFocusableElements (container) {
  return Array.from(
    container.querySelectorAll(
      'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe'
    )
  )
}

const trapFocusHandlers = {}

function trapFocus (container, elementToFocus = container) {
  const elements = getFocusableElements(container)
  const first = elements[0]
  const last = elements[elements.length - 1]

  removeTrapFocus()

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    ) {
      return
    }

    document.addEventListener('keydown', trapFocusHandlers.keydown)
  }

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown)
  }

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault()
      first.focus()
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault()
      last.focus()
    }
  }

  document.addEventListener('focusout', trapFocusHandlers.focusout)
  document.addEventListener('focusin', trapFocusHandlers.focusin)

  elementToFocus.focus()
}

function removeTrapFocus (elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin)
  document.removeEventListener('focusout', trapFocusHandlers.focusout)
  document.removeEventListener('keydown', trapFocusHandlers.keydown)

  if (elementToFocus) elementToFocus.focus()
}

function onKeyUpEscape (event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return

  const openDetailsElement = event.target.closest('details[open]')
  if (!openDetailsElement) return

  const summaryElement = openDetailsElement.querySelector('summary')
  openDetailsElement.removeAttribute('open')
  summaryElement.setAttribute('aria-expanded', false)
  summaryElement.focus()
}

function isJson (item) {
  let value = typeof item !== 'string' ? JSON.stringify(item) : item
  try {
    value = JSON.parse(value)
  } catch (e) {
    return false
  }

  return typeof value === 'object' && value !== null
}

/**
 * The function handles purchase limit errors by filtering and throwing the relevant errors.
 * @param product - The `product` parameter is an object that represents a product. It contains
 * properties such as `type`, `component_ids`, `id`, etc.
 * @param parsedState - parsedState is an object that contains information about the state of the
 * Ajax API response. It may have properties like "errors" or "message" which contain error information.
 */
function handlePurchaseLimitErrors (product, parsedState) {
  const filtered_errors = []
  const errors = parsedState.errors ? parsedState.errors : parsedState.message
  const is_json_response = isJson(errors)

  if (is_json_response) {
    // Purchase Limits Errors
    const products_with_errors = JSON.parse(errors)
    let product_ids = []

    if (product.component_ids) {
      product_ids = [...product.component_ids]
    } else {
      product_ids.push(product.id)
    }
    products_with_errors.forEach(product => {
      if (product_ids.includes(product.id)) {
        console.log(product)
        if (product.productLevelLimit !== null && product.error !== '') {
          filtered_errors.push(product.error)
        } else if (product.variants.length) {
          product.variants.forEach(variant => {
            console.log('variant', variant)
            if (variant.maxVariantLimit !== null) {
              if (variant.error !== '') {
                filtered_errors.push(variant.error)
              }
            }
          })
        }
      }
    })
  } else {
    // Standard shopify cart errors
    filtered_errors.push(parsedState.errors ? parsedState.errors : parsedState.description)
  }

  throw filtered_errors
}
function validateEmail (email) {
  // eslint-disable-next-line no-useless-escape
  const re = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(email)
}

/**
 * A JavaScript function that gets as close as possible to emulating the "money" filter of Shopify Liquid
 * @param amountInCents
 * @returns {*|string}
 */
function formatMoney (amountInCents) {
  const format = window.shopCurrencyFormat

  if (isNaN(amountInCents) || !format) return ''
  const amount = parseFloat((amountInCents / 100).toString())

  const hasDecimals = (format.includes('amount') && !format.includes('amount_')) ||
    format.includes('amount_with_comma_separator') ||
    format.includes('amount_with_apostrophe_separator') ||
    format.includes('amount_with_space_separator') ||
    format.includes('amount_with_period_and_space_separator')
  const decimalPlaces = hasDecimals ? 2 : 0

  // The following Regex matches the positions in-between all groups of three digits, where a thousands separator should be inserted.
  const addSeparators = (num, separator = ',') => num.replace(/\B(?=(\d{3})+(?!\d))/g, separator)
  // The following Regex matches any of the supported replace tokens of the type "{{ amount_* }}" regardless of whitespaces within the curly braces.
  const applyCurrencyFormat = (num, format) => format.replace(/{{\s*amount.*?\s*}}/, num)

  let fixedAmount = amount.toFixed(decimalPlaces)

  // Choose format pattern
  if (format.includes('amount_with_comma_separator')) {
    fixedAmount = addSeparators(fixedAmount.replace('.', ','), '.')
  } else if (format.includes('amount_no_decimals_with_comma_separator')) {
    fixedAmount = addSeparators(parseInt(amount).toString(), '.')
  } else if (format.includes('amount_with_apostrophe_separator')) {
    fixedAmount = addSeparators(fixedAmount, '\'')
  } else if (format.includes('amount_no_decimals_with_space_separator')) {
    fixedAmount = addSeparators(parseInt(amount).toString(), ' ')
  } else if (format.includes('amount_with_space_separator')) {
    fixedAmount = addSeparators(fixedAmount, ' ')
  } else if (format.includes('amount_with_period_and_space_separator')) {
    fixedAmount = addSeparators(fixedAmount.replace('.', ' '), ' ')
  } else if (format.includes('amount_no_decimals')) {
    fixedAmount = parseInt(amount).toString()
  } else {
    fixedAmount = addSeparators(fixedAmount)
  }

  return applyCurrencyFormat(fixedAmount, format)
}

export { throttle, debounce, serializeForm, fetchConfig, getFocusableElements, trapFocus, removeTrapFocus, onKeyUpEscape, isJson, handlePurchaseLimitErrors, validateEmail, formatMoney }
